import { Tabs as MuiTabs, Tab as MuiTab } from "@mui/material";
import type { ReactNode } from "react";

export type Tab = {
  /** Unique identifier — used as the controlled value. */
  id: string;
  label: ReactNode;
};

type TabsProps = {
  tabs: Tab[];
  /** Id of the currently active tab. */
  value: string;
  onChange: (id: string) => void;
  className?: string;
  /** When true, tabs share the available width equally and the strip
   * spans 100% of its container. */
  stretch?: boolean;
  /** Optional content rendered inside the pill, after the tab items. */
  trailing?: ReactNode;
};

/**
 * Brand tabs — wraps `@mui/material/Tabs` but renders the pill-style strip
 * the team designed: rounded outer container with grey-100 background,
 * active tab in black with white text + flat-black shadow, inactive tabs
 * in white with grey-200 border + flat shadow. MUI's default underline
 * indicator is hidden; the active visual is delivered entirely via per-tab
 * sx overrides keyed off the `Mui-selected` state.
 */
export function Tabs({
  tabs,
  value,
  onChange,
  className = "",
  stretch = false,
  trailing,
}: TabsProps) {
  // Stretch tabs fill their container (no overflow possible). Non-stretch tab
  // strips scroll horizontally when they exceed the available width instead of
  // clipping the trailing tabs out of reach on narrow screens.
  const scrollable = !stretch;
  return (
    <div
      className={`flex items-center overflow-hidden rounded-lg border border-grey-200 bg-grey-100 p-1 ${className}`}
    >
      <MuiTabs
        value={value}
        onChange={(_e, next: string) => onChange(next)}
        aria-label="tabs"
        variant={scrollable ? "scrollable" : "standard"}
        scrollButtons={false}
        // MUI v9 deprecated `TabIndicatorProps` (it leaked to the DOM as an
        // unknown attribute — a dev console warning). The indicator is hidden via
        // the modern `slotProps.indicator` instead; the active pill styling is
        // delivered entirely by the per-tab `Mui-selected` sx below.
        slotProps={{ indicator: { sx: { display: "none" } } }}
        sx={{
          minHeight: "unset",
          width: stretch ? "100%" : "auto",
          maxWidth: "100%",
          display: "flex",
          alignItems: "stretch",
          gap: "8px",
          // MUI renames this node across versions (flexContainer → list); target
          // both so the 4px inter-tab gap (Figma node 15728:51189) always applies.
          "& .MuiTabs-flexContainer, & .MuiTabs-list": {
            gap: "8px",
            width: stretch ? "100%" : "auto",
          },
          "& .MuiTabs-scroller": {
            width: stretch ? "100%" : "auto",
            // Touch/trackpad horizontal scroll without a visible scrollbar.
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          },
        }}
      >
        {tabs.map((t) => (
          <MuiTab
            key={t.id}
            value={t.id}
            label={t.label}
            disableRipple
            sx={{
              minHeight: "unset",
              minWidth: stretch ? 0 : "64px",
              flex: stretch ? 1 : "0 0 auto",
              padding: "6px 12px", // py-1.5 px-3
              fontSize: "14px", // text-small
              lineHeight: "20px",
              fontWeight: 500,
              letterSpacing: "-0.01em",
              textTransform: "none",
              borderRadius: "4px",
              transition: "background-color 120ms, color 120ms, border-color 120ms",
              // INACTIVE
              color: "#2d343c", // black
              backgroundColor: "#ffffff",
              border: "1px solid #e5ebf1", // grey-200
              boxShadow: "0px 1px 3px #181d2214", // shadow-flat
              "&:hover": {
                backgroundColor: "#f6f8fa", // grey-50
              },
              // ACTIVE (Mui-selected)
              "&.Mui-selected": {
                color: "#ffffff",
                backgroundColor: "#384049", // grey-950
                borderTop: "1px solid #4e5966", // grey-900
                borderRight: "0",
                borderBottom: "0",
                borderLeft: "0",
                boxShadow: "0px 1px 3px #2d343c52", // shadow-flat-black
              },
            }}
          />
        ))}
      </MuiTabs>
      {trailing && <div className="ml-2">{trailing}</div>}
    </div>
  );
}
